import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableSortLabel,
  Checkbox,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Alert,
} from '@mui/material'
import { ListPagination } from '../components/ListPagination'
import { Search, FileDownload } from '@mui/icons-material'
import { TRACK_EVENTS, US_STATES, EXPORT_MAX_STUDENTS } from '@tnf-recruit/shared'
import { useStudentSearch, useStudentSearchIds } from '../hooks/useStudentSearch'
import { useStudentExport } from '../hooks/useStudentExport'
import { AppPageHeader } from '../components/AppPageHeader'
import { LoadingStatePanel } from '../components/StatePanel'

/** Look up display name for an event ID */
const getEventName = (eventId: string) => TRACK_EVENTS.find(e => e.id === eventId)?.name ?? eventId

const ATHLETES_PER_PAGE = 20

export const BrowsePage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [graduationYear, setGraduationYear] = useState('')
  const [minGPA, setMinGPA] = useState('')
  const [selectedEventId, setSelectedEventId] = useState('')
  const [eventCutoff, setEventCutoff] = useState('')
  const [minSatScore, setMinSatScore] = useState('')
  const [includeMissingSatScore, setIncludeMissingSatScore] = useState(true)
  const [minActScore, setMinActScore] = useState('')
  const [includeMissingActScore, setIncludeMissingActScore] = useState(true)
  const [competitionTeam, setCompetitionTeam] = useState<'' | 'men' | 'women'>('')
  const [state, setState] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<
    'name' | 'graduationYear' | 'gpa' | 'satScore' | 'actScore' | 'personalRecord'
  >('personalRecord')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  // Keyed by student id so selection survives pagination/refetches
  const [selected, setSelected] = useState<Map<string, string>>(new Map())
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error' | 'info'
    message: string
  } | null>(null)
  const [confirmSelectAllOpen, setConfirmSelectAllOpen] = useState(false)
  const navigate = useNavigate()

  const gradYears: number[] = []
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  if (currentDate.getMonth() < 6) {
    gradYears.push(currentYear, currentYear + 1, currentYear + 2, currentYear + 3)
  } else {
    gradYears.push(currentYear + 1, currentYear + 2, currentYear + 3, currentYear + 4)
  }

  // Build search params from filters
  const searchParams = {
    query: searchQuery || undefined,
    eventId: selectedEventId || undefined,
    eventCutoff: eventCutoff ? Number(eventCutoff) : undefined,
    graduationYear: graduationYear ? Number(graduationYear) : undefined,
    minGPA: minGPA ? Number(minGPA) : undefined,
    minSatScore: minSatScore ? Number(minSatScore) : undefined,
    includeMissingSatScore,
    minActScore: minActScore ? Number(minActScore) : undefined,
    includeMissingActScore,
    competitionTeam: competitionTeam || undefined,
    state: state || undefined,
    sortBy,
    sortOrder,
    page,
    limit: ATHLETES_PER_PAGE,
  }

  const { data: searchResult, isLoading } = useStudentSearch(searchParams)
  const students = searchResult?.data ?? []
  const pagination = searchResult?.pagination

  const searchIdsQuery = useStudentSearchIds(searchParams)
  const exportMutation = useStudentExport()

  const studentName = (s: (typeof students)[number]) =>
    `${s.personalInfo?.firstName ?? ''} ${s.personalInfo?.lastName ?? ''}`.trim()

  // Page-local: whether every row currently rendered is selected/some are.
  const allOnPageSelected = students.length > 0 && students.every(s => selected.has(s.id))
  const someOnPageSelected = students.some(s => selected.has(s.id))
  // Whole result set: whether the selection already covers every match (or the
  // export cap, whichever is smaller) — this is what the header checkbox reflects.
  const headerChecked =
    allOnPageSelected &&
    !!pagination &&
    selected.size >= Math.min(pagination.total, EXPORT_MAX_STUDENTS)
  const headerIndeterminate = someOnPageSelected && !headerChecked

  const toggleRow = (id: string, name: string) => {
    setSelected(prev => {
      const next = new Map(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.set(id, name)
      }
      return next
    })
  }

  const clearSelection = () => {
    setSelected(new Map())
    setFeedback(null)
  }

  const handleSelectAllMatching = async () => {
    const result = await searchIdsQuery.refetch()
    const ids = result.data?.ids ?? []
    setSelected(prev => {
      const next = new Map(prev)
      ids.forEach(id => {
        if (!next.has(id)) next.set(id, '')
      })
      return next
    })
    if (result.data?.capped) {
      setFeedback({
        severity: 'info',
        message: `Selected the first ${ids.length} of ${result.data.total} matching athletes (export limit).`,
      })
    } else {
      setFeedback(null)
    }
  }

  // Single control: unchecked/indeterminate -> select everything matching (all
  // on this page directly if that's already everything, otherwise confirm first
  // since it can span many pages); checked -> clear the whole selection.
  const handleHeaderCheckboxClick = () => {
    if (headerChecked) {
      clearSelection()
      return
    }
    if (!pagination) return
    if (pagination.total <= students.length) {
      setSelected(prev => {
        const next = new Map(prev)
        students.forEach(s => next.set(s.id, studentName(s)))
        return next
      })
      return
    }
    setConfirmSelectAllOpen(true)
  }

  const confirmSelectAllMatching = async () => {
    await handleSelectAllMatching()
    setConfirmSelectAllOpen(false)
  }

  const handleExport = () => {
    const studentIds = Array.from(selected.keys())
    setFeedback(null)
    exportMutation.mutate(studentIds, {
      onSuccess: result => {
        setSelected(new Map())
        if (result.exportedCount < result.requestedCount) {
          setFeedback({
            severity: 'success',
            message: `Exported ${result.exportedCount} of ${result.requestedCount} selected — the rest are no longer public.`,
          })
        } else {
          setFeedback({
            severity: 'success',
            message: `Exported ${result.exportedCount} athlete${result.exportedCount === 1 ? '' : 's'}.`,
          })
        }
      },
      onError: err => {
        setFeedback({ severity: 'error', message: err.message })
      },
    })
  }

  const selectedEventMeta = selectedEventId
    ? TRACK_EVENTS.find(event => event.id === selectedEventId)
    : undefined

  const hasActiveFilters = !!(
    selectedEventId ||
    eventCutoff ||
    competitionTeam ||
    minGPA ||
    minSatScore ||
    minActScore ||
    graduationYear ||
    state
  )

  const handleSort = (
    key: 'name' | 'graduationYear' | 'gpa' | 'satScore' | 'actScore' | 'personalRecord'
  ) => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortOrder(key === 'name' ? 'asc' : 'desc')
    }
    setPage(1)
  }

  const clearFilters = () => {
    setGraduationYear('')
    setMinGPA('')
    setSelectedEventId('')
    setEventCutoff('')
    setMinSatScore('')
    setMinActScore('')
    setIncludeMissingSatScore(true)
    setIncludeMissingActScore(true)
    setCompetitionTeam('')
    setState('')
    setPage(1)
  }

  return (
    <Box>
      <AppPageHeader
        title="Browse Athletes"
        description="Discover talented student-athletes from across the country."
      />

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by name or school"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel shrink>Graduation Year</InputLabel>
                <Select
                  value={graduationYear}
                  label="Graduation Year"
                  displayEmpty
                  onChange={e => {
                    setGraduationYear(e.target.value)
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {gradYears.map(year => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel shrink>Team</InputLabel>
                <Select
                  value={competitionTeam}
                  label="Team"
                  displayEmpty
                  onChange={e => {
                    setCompetitionTeam(e.target.value as '' | 'men' | 'women')
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All Teams</MenuItem>
                  <MenuItem value="men">Men&apos;s Team</MenuItem>
                  <MenuItem value="women">Women&apos;s Team</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                sx={{ width: '100%', height: 56 }}
                onClick={() => setFiltersOpen(true)}
              >
                More Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" sx={{ width: '100%', height: 56 }} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedEventId && (
                <Chip size="small" label={`Event: ${getEventName(selectedEventId)}`} />
              )}
              {eventCutoff &&
                (selectedEventMeta?.isFieldEvent ? (
                  <Chip size="small" label={`Mark ≥ ${eventCutoff}`} />
                ) : (
                  <Chip size="small" label={`Time ≤ ${eventCutoff}`} />
                ))}
              {competitionTeam && (
                <Chip
                  size="small"
                  label={`Team: ${competitionTeam === 'men' ? "Men's" : "Women's"}`}
                />
              )}
              {minGPA && <Chip size="small" label={`GPA ≥ ${minGPA}`} />}
              {minSatScore && <Chip size="small" label={`SAT ≥ ${minSatScore}`} />}
              {minActScore && <Chip size="small" label={`ACT ≥ ${minActScore}`} />}
              {graduationYear && <Chip size="small" label={`Grad Year: ${graduationYear}`} />}
              {state && <Chip size="small" label={`State: ${state}`} />}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Search Filters</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>State</InputLabel>
                <Select
                  value={state}
                  label="State"
                  displayEmpty
                  onChange={e => {
                    setState(e.target.value)
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All States</MenuItem>
                  {US_STATES.map(s => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel shrink>Primary Event</InputLabel>
                <Select
                  value={selectedEventId}
                  label="Primary Event"
                  displayEmpty
                  onChange={e => {
                    setSelectedEventId(e.target.value)
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All Events</MenuItem>
                  {TRACK_EVENTS.map(event => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedEventId && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={selectedEventMeta?.isFieldEvent ? 'Minimum Mark' : 'Maximum Time'}
                  type="number"
                  value={eventCutoff}
                  onChange={e => {
                    setEventCutoff(e.target.value)
                    setPage(1)
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum GPA"
                type="number"
                value={minGPA}
                onChange={e => {
                  setMinGPA(e.target.value)
                  setPage(1)
                }}
                inputProps={{ min: 0, max: 4, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum SAT"
                type="number"
                value={minSatScore}
                onChange={e => {
                  setMinSatScore(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          minSatScore ? 'Include students who did not report an SAT score' : ''
                        }
                      >
                        <span>
                          <Checkbox
                            size="small"
                            disabled={!minSatScore}
                            checked={includeMissingSatScore}
                            onChange={e => {
                              setIncludeMissingSatScore(e.target.checked)
                              setPage(1)
                            }}
                            sx={{ opacity: minSatScore ? 1 : 0.35 }}
                          />
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum ACT"
                type="number"
                value={minActScore}
                onChange={e => {
                  setMinActScore(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          minActScore ? 'Include students who did not report an ACT score' : ''
                        }
                      >
                        <span>
                          <Checkbox
                            size="small"
                            disabled={!minActScore}
                            checked={includeMissingActScore}
                            onChange={e => {
                              setIncludeMissingActScore(e.target.checked)
                              setPage(1)
                            }}
                            sx={{ opacity: minActScore ? 1 : 0.35 }}
                          />
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={clearFilters}>
            Clear
          </Button>
          <Button variant="contained" onClick={() => setFiltersOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSelectAllOpen} onClose={() => setConfirmSelectAllOpen(false)}>
        <DialogTitle>Select all matching athletes?</DialogTitle>
        <DialogContent>
          You&apos;re about to select {pagination?.total}{' '}
          {pagination?.total === 1 ? 'athlete' : 'athletes'} for export
          {pagination && pagination.total > EXPORT_MAX_STUDENTS
            ? ` (capped at ${EXPORT_MAX_STUDENTS})`
            : ''}
          .
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmSelectAllOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSelectAllMatching}
            disabled={searchIdsQuery.isFetching}
          >
            {searchIdsQuery.isFetching ? 'Selecting…' : 'Select All'}
          </Button>
        </DialogActions>
      </Dialog>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      {selected.size > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              py: '8px !important',
            }}
          >
            <Box sx={{ fontWeight: 'medium', pr: 2 }}>{selected.size} selected</Box>
            <Button size="small" variant="outlined" onClick={clearSelection}>
              Clear
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? 'Exporting…' : 'Export to CSV'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <LoadingStatePanel
          title="Loading athletes"
          description="Applying filters and sorting your athlete results."
        />
      )}

      {/* Results */}
      {!isLoading && (
        <Card>
          <TableContainer component={Paper} sx={{ border: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={headerIndeterminate}
                      checked={headerChecked}
                      onChange={handleHeaderCheckboxClick}
                    />
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'name' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>School</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Primary Events
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'graduationYear' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'graduationYear'}
                      direction={sortBy === 'graduationYear' ? sortOrder : 'asc'}
                      onClick={() => handleSort('graduationYear')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Grad Year
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'gpa' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'gpa'}
                      direction={sortBy === 'gpa' ? sortOrder : 'asc'}
                      onClick={() => handleSort('gpa')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      GPA
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'satScore' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'satScore'}
                      direction={sortBy === 'satScore' ? sortOrder : 'asc'}
                      onClick={() => handleSort('satScore')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      SAT
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'actScore' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'actScore'}
                      direction={sortBy === 'actScore' ? sortOrder : 'asc'}
                      onClick={() => handleSort('actScore')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      ACT
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === 'personalRecord' ? sortOrder : false}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={sortBy === 'personalRecord'}
                      direction={sortBy === 'personalRecord' ? sortOrder : 'asc'}
                      onClick={() => handleSort('personalRecord')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Personal Record
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>State</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map(student => {
                  const pi = student.personalInfo ?? {}
                  const ai = student.academicInfo ?? {}
                  const ath = student.athleticInfo ?? {}
                  const records = student.personalRecords ?? []
                  const primaryEvents: string[] = ath.primaryEvents ?? []
                  const stateCode = (student as any).state ?? pi.address?.state ?? ''
                  const selectedEventRecord = selectedEventId
                    ? records.find((r: any) => r.eventId === selectedEventId)
                    : undefined
                  return (
                    <TableRow key={student.id} hover selected={selected.has(student.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(student.id)}
                          onChange={() => toggleRow(student.id, studentName(student))}
                        />
                      </TableCell>
                      <TableCell>
                        {pi.firstName} {pi.lastName}
                      </TableCell>
                      <TableCell>{ai.highSchool || '—'}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {primaryEvents.slice(0, 3).map((eventId: string) => (
                            <Chip key={eventId} label={getEventName(eventId)} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(student as any).graduationYear ?? ai.graduationYear ?? '—'}
                      </TableCell>
                      <TableCell>{(student as any).gpa ?? ai.gpa ?? '—'}</TableCell>
                      <TableCell>{ai.satScore ?? '—'}</TableCell>
                      <TableCell>{ai.actScore ?? '—'}</TableCell>
                      <TableCell>{selectedEventRecord?.mark ?? '—'}</TableCell>
                      <TableCell>
                        {(student as any).competitionTeam
                          ? (student as any).competitionTeam === 'men'
                            ? "Men's"
                            : "Women's"
                          : '—'}
                      </TableCell>
                      <TableCell>{stateCode || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`/student/${student.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {students.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No athletes found. Try adjusting your filters or clearing search criteria.
            </Box>
          )}
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <ListPagination count={pagination.totalPages} page={page} onChange={setPage} />
        </Box>
      )}
    </Box>
  )
}
